import { FC, useMemo } from 'react';

import { Box } from '@mui/material';
import { GridRowProps } from '@mui/x-data-grid';

import { useQuery } from '@redwoodjs/web';

import { NPIDataGrid } from 'src/components/NPIDataGrid/NPIDataGrid';
import ReusableGrid from 'src/components/ReusableGrid';
import { showErrorToast } from 'src/components/Toasts/toast';
import {
    GET_CHECK_IN_CALLS_FOR_SERVICE,
    GET_CHECK_IN_CALLS_FOR_SERVICE_FOR_MERCHANT,
} from 'src/graphql/queries/getCheckInCalls.query';
import { resolvePageSize } from 'src/lib/pagination';
import CheckInCallHistoryColumnDef from 'src/pages/ViewServicePage/components/CheckInCallHistoryColumnDef';
import { Note } from 'types/graphql';

interface CheckinCallHistoryProps {
    serviceId: number;
    merchantId: number;
}

const COMPONENT_TITLE = 'Check-In Call History';
const PAGE_SIZE = 10;

const CheckinCallHistory: FC<CheckinCallHistoryProps> = ({
    serviceId = null,
    merchantId = null,
    onOpen,
}: {
    serviceId: number;
    merchantId: number;
    onOpen: (dateTime, subject, addedBy, id, body) => void;
}) => {
    const { data: checkInCallHistory } = useQuery(GET_CHECK_IN_CALLS_FOR_SERVICE, {
        skip: !serviceId,
        onError: () => {
            showErrorToast('Error fetching check-in call history');
        },
        variables: { serviceId },
    });

    const { data: checkInCallHistoryForMerchant } = useQuery(
        GET_CHECK_IN_CALLS_FOR_SERVICE_FOR_MERCHANT,
        {
            skip: !merchantId,
            onError: () => {
                showErrorToast('Error fetching check-in call history for merchant');
            },
            variables: { merchantId },
        }
    );

    const columns = useMemo(() => {
        return CheckInCallHistoryColumnDef();
    }, []);

    const gridData = useMemo(() => {
        const data =
            checkInCallHistory?.getCheckInCallNotesForService ??
            checkInCallHistoryForMerchant?.getCheckInCallNotesForForMerchant ??
            [];
        return (
            data.map((item: Note) => {
                return {
                    ...item,
                    fullName:
                        `${item.createdBy?.firstName ?? ''} ${item.createdBy?.lastName ?? ''}`.trim(),
                };
            }) ?? []
        );
    }, [checkInCallHistory, checkInCallHistoryForMerchant]);

    return (
        <Box
            mt={4}
            width="100%"
            data-testid="checkin-call-history-section"
            sx={{
                '& .MuiDataGrid-toolbarContainer': { display: 'none' },
            }}
        >
            <ReusableGrid title={COMPONENT_TITLE} xs={12}>
                <NPIDataGrid
                    rows={gridData ?? []}
                    columns={columns}
                    hiddenColumns={['id']}
                    getRowId={(row: GridRowProps) => {
                        return row.id;
                    }}
                    handleView={(data: any) => {
                        onOpen(
                            data?.createdAt,
                            data?.subject,
                            `${data?.createdBy?.firstName ?? ''} ${data?.createdBy?.lastName ?? ''}`.trim(),
                            data?.id,
                            data?.body
                        );
                    }}
                    dataTestId={'npi-data-grid-check-in-call-history'}
                    key={'npi-data-grid-check-in-call-history'}
                    pagination={{ isEnabled: true, pageSize: resolvePageSize(PAGE_SIZE) }}
                    title={COMPONENT_TITLE}
                />
            </ReusableGrid>
        </Box>
    );
};

export default CheckinCallHistory;
